import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const I18nContext = createContext(null);

const dict = {
  en: {
    nav: { shop: "Shop", collections: "Collections", about: "About", contact: "Contact", admin: "Admin" },
    hero: {
      kicker: "Saint Barthélemy — Est. 2026",
      line1: "Make the world",
      line2: "great again.",
      line3: "The return of the king.",
      cta_primary: "Enter the throne",
      cta_secondary: "Our story",
    },
    marquee: "BEAUDOIN · LIMITED EDITIONS · MONTHLY GIVEAWAY FOR EVERY CUSTOMER · GOTHIC LUXURY · ",
    featured: { kicker: "Latest drop", title: "The Sovereign Collection" },
    pillars: {
      kicker: "Why BAUDOIN",
      items: [
        { t: "Monthly Giveaway — auto-entry", d: "Every order is automatically entered into our current monthly giveaway. One winner. One free piece. Every month. No code needed." },
        { t: "Limited series only", d: "We never restock. When it's gone, it belongs to history." },
        { t: "Solid sterling silver", d: "Every metal piece is hallmarked 925. No plating, no compromise." },
        { t: "Worldwide express", d: "Discreet delivery in 48-72h, fully insured, signature required." },
      ],
    },
    about: {
      kicker: "Our story",
      title: "A house born on volcanic rock.",
      body1: "Beaudoin was conceived on the black sand beaches of Saint Barthélemy by a generation that grew up between the gothic codes of European nobility and the raw freedom of the Caribbean.",
      body2: "We dress kings who do not need crowns. Our pieces are heirlooms — heavy, slow, made to outlive trends.",
    },
    faq: {
      kicker: "Questions",
      items: [
        { q: "Where do you ship?", a: "Worldwide. Express delivery in 48-72h with signature." },
        { q: "Are returns accepted?", a: "Yes — 14 days from delivery, untouched and in original packaging." },
        { q: "How are sizes measured?", a: "Our pieces run true to European sizing. See each product page for the size guide." },
        { q: "Is the silver real?", a: "All metal pieces are solid 925 sterling silver, hallmarked." },
        { q: "Can I order custom pieces?", a: "Yes — write to atelier@baudoin.com for bespoke commissions." },
      ],
    },
    cta_footer: { title: "Wear the throne.", sub: "Limited drops. No restock. Once it's gone, it's gone.", btn: "Discover the collection" },
    cart: { title: "Your selection", empty: "Your selection is empty.", subtotal: "Subtotal", checkout: "Checkout on Shopify", continue: "Continue shopping", shopify_note: "These items will open Beaudoin's Shopify store. Variant IDs are not linked yet — once linked from the admin, the cart will pre-fill on Shopify automatically." },
    checkout: { title: "Checkout", contact: "Contact", shipping: "Shipping", pay: "Pay with SumUp", processing: "Creating order…" },
    product: { add: "Add to cart", select_size: "Select a size", size: "Size", description: "Description" },
    contact: { title: "Contact", subtitle: "Atelier inquiries, press, bespoke commissions.", name: "Full name", email: "Email", message: "Your message", send: "Send", thanks: "Message sent. We'll reply within 48h." },
    footer: { newsletter: "Join the brotherhood", placeholder: "Your email", subscribe: "Subscribe", rights: "All rights reserved.", legal: "Legal" },
    order: { processing: "Verifying your payment…", success: "Payment received. The throne is yours.", failed: "Payment did not complete.", back: "Back to shop", order_id: "Order ID", total: "Total" },
  },
  fr: {
    nav: { shop: "Boutique", collections: "Collections", about: "Maison", contact: "Contact", admin: "Admin" },
    hero: {
      kicker: "Saint Barthélemy — Depuis 2026",
      line1: "Make the world",
      line2: "great again.",
      line3: "The return of the king.",
      cta_primary: "Entrer dans le trône",
      cta_secondary: "Notre histoire",
    },
    marquee: "BEAUDOIN · ÉDITIONS LIMITÉES · TIRAGE AU SORT MENSUEL POUR CHAQUE CLIENT · LUXE GOTHIQUE · ",
    featured: { kicker: "Dernière collection", title: "La Collection Souveraine" },
    pillars: {
      kicker: "Pourquoi BAUDOIN",
      items: [
        { t: "Concours mensuel — inscription auto", d: "Chaque commande est automatiquement inscrite à notre concours en cours. Un gagnant. Une pièce offerte. Chaque mois. Aucun code requis." },
        { t: "Séries limitées uniquement", d: "Aucun réassort. Une fois épuisée, la pièce entre dans l'histoire." },
        { t: "Argent massif 925", d: "Chaque pièce métallique est poinçonnée 925. Sans placage, sans compromis." },
        { t: "Livraison express mondiale", d: "Livraison discrète 48-72h, assurée, signature obligatoire." },
      ],
    },
    about: {
      kicker: "Notre histoire",
      title: "Une maison née sur la roche volcanique.",
      body1: "BAUDOIN est née sur les plages de sable noir de Saint-Barthélemy, par une génération élevée entre les codes gothiques de la noblesse européenne et la liberté brute des Caraïbes.",
      body2: "Nous habillons des rois qui n'ont pas besoin de couronne. Nos pièces sont des héritages — lourdes, lentes, conçues pour traverser les modes.",
    },
    faq: {
      kicker: "Questions",
      items: [
        { q: "Où livrez-vous ?", a: "Partout dans le monde. Livraison express 48-72h avec signature." },
        { q: "Acceptez-vous les retours ?", a: "Oui — 14 jours après réception, en état neuf et emballage d'origine." },
        { q: "Comment sont les tailles ?", a: "Les pièces taillent juste, normes européennes. Guide des tailles sur chaque fiche." },
        { q: "L'argent est-il véritable ?", a: "Toutes les pièces métalliques sont en argent massif 925, poinçonné." },
        { q: "Pièces sur-mesure ?", a: "Oui — écrivez à atelier@baudoin.com pour toute commande spéciale." },
      ],
    },
    cta_footer: { title: "Portez le trône.", sub: "Séries limitées. Aucun réassort. Une fois épuisée, elle disparaît.", btn: "Découvrir la collection" },
    cart: { title: "Votre sélection", empty: "Votre sélection est vide.", subtotal: "Sous-total", checkout: "Finaliser sur Shopify", continue: "Continuer mes achats", shopify_note: "Ces articles ouvriront la boutique Shopify Beaudoin. Les variants ne sont pas encore reliés — une fois reliés depuis l'admin, le panier se pré-remplira automatiquement sur Shopify." },
    checkout: { title: "Commande", contact: "Coordonnées", shipping: "Livraison", pay: "Payer avec SumUp", processing: "Création de la commande…" },
    product: { add: "Ajouter au panier", select_size: "Choisir une taille", size: "Taille", description: "Description" },
    contact: { title: "Contact", subtitle: "Atelier, presse, commandes spéciales.", name: "Nom complet", email: "Email", message: "Votre message", send: "Envoyer", thanks: "Message envoyé. Réponse sous 48h." },
    footer: { newsletter: "Rejoignez la confrérie", placeholder: "Votre email", subscribe: "S'abonner", rights: "Tous droits réservés.", legal: "Mentions légales" },
    order: { processing: "Vérification du paiement…", success: "Paiement reçu. Le trône est à vous.", failed: "Le paiement n'a pas abouti.", back: "Retour à la boutique", order_id: "Référence", total: "Total" },
  },
};

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("baudoin_lang") || "en");
  useEffect(() => { localStorage.setItem("baudoin_lang", lang); }, [lang]);
  const t = useMemo(() => dict[lang], [lang]);
  return <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
